// Money in IDR (screaming.md → "The upsert trap"). Three forms, one meaning:
//   wire / column : "190000.00" — a decimal string, exactly two decimals
//   in process    : Dinero<number> in sen — every add, multiply, compare goes through dinero.js
// parseMoney and formatMoney are the only conversions. No Number(), no parseFloat on money.
import { IDR, dinero, toSnapshot, transformScale, trimScale, type Dinero } from "dinero.js";
import { z } from "zod";

export type { Dinero } from "dinero.js";

/** The openapi `Money` pattern: non-negative, exactly two decimals. */
export const MONEY_PATTERN = /^\d+\.\d{2}$/;
const SIGNED = /^(-?)(\d+)\.(\d{2})$/;

/** A non-negative IDR amount as it travels on the wire. */
export const Money = z.string().regex(MONEY_PATTERN, "invalid_money").brand<"Money">();
export type Money = z.infer<typeof Money>;

/** An IDR amount from a count of sen. For constants and tests; real amounts come from parseMoney. */
export function idr(sen: number): Dinero<number> {
  if (!Number.isSafeInteger(sen)) throw new RangeError(`not a whole number of sen: ${sen}`);
  return dinero({ amount: sen, currency: IDR });
}

/** A wire string or a numeric(18,2) column value, signed, into a Dinero. */
export function parseMoney(value: string): Dinero<number> {
  const match = SIGNED.exec(value);
  if (!match) throw new TypeError(`not a two-decimal IDR amount: ${JSON.stringify(value)}`);
  const [, sign, units, cents] = match;
  const sen = BigInt(`${units}${cents}`);
  if (sen > BigInt(Number.MAX_SAFE_INTEGER)) throw new RangeError(`IDR amount too large: ${value}`);
  return idr(sign === "-" ? -Number(sen) : Number(sen));
}

/**
 * A Dinero into the two-decimal string the wire and the column take.
 * A fraction of a sen is refused, not rounded: the caller chooses the rounding
 * (transformScale with halfEven, up, …) because only the caller knows which is right.
 */
export function formatMoney(money: Dinero<number>): string {
  const { currency, scale } = toSnapshot(trimScale(money));
  if (currency.code !== "IDR") throw new TypeError(`not IDR: ${currency.code}`);
  if (scale > 2) throw new RangeError("fraction of a sen: round to scale 2 before formatting");
  const { amount } = toSnapshot(transformScale(money, 2));
  const abs = Math.abs(amount);
  const units = Math.trunc(abs / 100);
  const cents = String(abs % 100).padStart(2, "0");
  return `${amount < 0 ? "-" : ""}${units}.${cents}`;
}
